<template>
  <div v-if="inline" class="highlight-benefits-modal--inline-wrap">
    <div class="highlight-benefits-modal" @click.stop>
      <HighlightBenefitsModalBody
        :title="title"
        :deal-name="dealName"
        :formatted-balance="formattedBalance"
        :can-pay-with-points="canPayWithPoints"
        :points-cost="pointsCost"
        :points-redeem-label="pointsRedeemLabel"
        :points-collect-label="pointsCollectLabel"
        :price-button-label="priceButtonLabel"
        :footer-primary-label="footerPrimaryLabel"
        :show-tooltip="showTooltip"
        :tooltip-lines="tooltipLines"
        :format-tooltip-item="formatTooltipItem"
        @dismiss="dismiss"
        @confirm-points="confirmPoints"
        @confirm-stripe="confirmStripe"
        @footer-primary="onFooterPrimary"
        @toggle-tooltip="toggleTooltip"
      />
    </div>
  </div>
  <div v-else class="highlight-benefits-modal" @click.stop>
    <HighlightBenefitsModalBody
          :title="title"
          :deal-name="dealName"
          :formatted-balance="formattedBalance"
          :can-pay-with-points="canPayWithPoints"
          :points-cost="pointsCost"
          :points-redeem-label="pointsRedeemLabel"
          :points-collect-label="pointsCollectLabel"
          :price-button-label="priceButtonLabel"
          :footer-primary-label="footerPrimaryLabel"
          :show-tooltip="showTooltip"
          :tooltip-lines="tooltipLines"
          :format-tooltip-item="formatTooltipItem"
          @dismiss="dismiss"
          @confirm-points="confirmPoints"
          @confirm-stripe="confirmStripe"
          @footer-primary="onFooterPrimary"
          @toggle-tooltip="toggleTooltip"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { modalController } from '@ionic/vue';
import HighlightBenefitsModalBody from '@/components/partner/HighlightBenefitsModalBody.vue';
import {
  buildPartnerPointsTooltipLines,
  formatHighlightPointsCollectLabel,
  formatHighlightPointsRedeemLabel,
  formatHighlightPriceButtonLabel,
  formatPointsBalanceMockup,
  getHighlightModalTitle,
} from '@/components/partner/partnerMembershipCopy.js';

const props = defineProps({
  product: { type: Object, required: true },
  availablePoints: { type: Number, default: 0 },
  dealName: { type: String, default: '' },
  inline: { type: Boolean, default: false },
});

const store = useStore();

const emit = defineEmits(['dismiss']);

const showTooltip = ref(false);

const pointsCost = computed(() => Number(props.product?.pointsCost ?? 0));
const priceEur = computed(() => Number(props.product?.priceEur ?? 0));
const canPayWithPoints = computed(() => (
  pointsCost.value > 0 && Number(props.availablePoints || 0) >= pointsCost.value
));
const pointsNeeded = computed(() => Math.max(
  0,
  Math.round((pointsCost.value - Number(props.availablePoints || 0)) * 100) / 100,
));

const title = computed(() => getHighlightModalTitle(props.product?.key));
const formattedBalance = computed(() => formatPointsBalanceMockup(props.availablePoints));
const pointsRedeemLabel = computed(() => formatHighlightPointsRedeemLabel(pointsCost.value));
const pointsCollectLabel = computed(() => formatHighlightPointsCollectLabel(pointsNeeded.value));
const priceButtonLabel = computed(() => formatHighlightPriceButtonLabel(priceEur.value));
const footerPrimaryLabel = computed(() => (
  canPayWithPoints.value ? 'Einlösen' : 'Bestellen'
));
const tooltipLines = computed(() => {
  const pointsEarn = store.getters['partner/getPointsEarn'];
  return buildPartnerPointsTooltipLines({
    highlights: store.getters['partner/getHighlights'],
    invitationAmount: pointsEarn?.invitationAmount,
    subscriptionMonthlyAmount: pointsEarn?.subscriptionMonthlyAmount,
  });
});

const formatTooltipItem = (text) => {
  const parts = text.split(/(\d+(?:\.\d+)?P(?: pro Monat)?)/g);
  return parts.map((part) => (
    /^\d+(?:\.\d+)?P/.test(part)
      ? `<span class="card-info-tooltip__highlight">${part}</span>`
      : part
  )).join('');
};

const resolveDismiss = (data = null, role = 'cancel') => {
  if (props.inline) {
    emit('dismiss', { data, role });
    return;
  }
  modalController.dismiss(data, role);
};

const dismiss = () => resolveDismiss(null, 'cancel');

const toggleTooltip = () => {
  showTooltip.value = !showTooltip.value;
};

const confirmPoints = () => {
  if (!canPayWithPoints.value) return;
  resolveDismiss({ paymentMethod: 'points' }, 'points');
};

const confirmStripe = () => {
  resolveDismiss({ paymentMethod: 'stripe' }, 'stripe');
};

const onFooterPrimary = () => {
  if (canPayWithPoints.value) {
    confirmPoints();
    return;
  }
  confirmStripe();
};
</script>

<style lang="scss" scoped>
.highlight-benefits-modal {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 340px;
  background: #fff;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14);
  padding: 22px 20px 0;
}
</style>

<style lang="scss">
@use '@/styles/card-info-tooltip.scss';
</style>
